/**
 * useSeries — hook for competition series data, real-time listeners, and CRUD.
 * Series aggregate results from multiple competitions with configurable scoring;
 * the aggregation itself is `getSeriesStandings` in `@sendly/shared` (the MCP
 * server runs the same one), loaded for the views by `useSeriesStandings`.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import {
  collection, doc, setDoc, updateDoc, onSnapshot, serverTimestamp
} from "firebase/firestore";
import { db } from "../firebase";
import { useGym } from "../contexts/GymContext";
import { buildSeriesDoc } from "@sendly/shared";
import { callFunction } from "../utils/callFunction";

export function useSeries() {
  const { gymId, gymMeta } = useGym();
  const [seriesList, setSeriesList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Real-time listener for series collection
  useEffect(() => {
    if (!gymId) { setLoading(false); return; }
    const col = collection(db, "gyms", gymId, "series");
    let initialized = false;
    const unsub = onSnapshot(col, snap => {
      if (initialized && snap.docChanges().length === 0) return;
      initialized = true;
      setSeriesList(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [gymId]);

  const createSeries = useCallback(async (data) => {
    if (!gymId) return null;
    const id = `series_${Date.now()}`;
    const ref = doc(db, "gyms", gymId, "series", id);
    // The field set (and the always-`active` status) is the shared builder's —
    // the MCP server creates series with the same one. `country` decides the
    // default language the description mirrors (gymDefaultLang).
    await setDoc(ref, {
      ...buildSeriesDoc(data, { gymId, country: gymMeta?.country }),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return id;
  }, [gymId, gymMeta]);

  const updateSeries = useCallback(async (seriesId, data) => {
    if (!gymId) return;
    const ref = doc(db, "gyms", gymId, "series", seriesId);
    await updateDoc(ref, {
      ...data,
      updatedAt: serverTimestamp(),
    });
  }, [gymId]);

  // Server-side: the series document has no subcollections, but its cover
  // image lives in Storage under `gyms/{gymId}/series/{seriesId}/`, and a
  // client cannot list a Storage folder without a CORS configuration the
  // bucket does not have. The CF deletes both.
  const deleteSeries = useCallback(async (seriesId) => {
    if (!gymId) return;
    await callFunction("deleteSeriesCascade", { gymId, seriesId });
  }, [gymId]);

  const closeSeries = useCallback(async (seriesId) => {
    if (!gymId) return;
    const ref = doc(db, "gyms", gymId, "series", seriesId);
    await updateDoc(ref, {
      status: "closed",
      closedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
  }, [gymId]);

  // Sorted: active first, then by updatedAt desc
  const sortedSeries = useMemo(() => {
    return [...seriesList].sort((a, b) => {
      if (a.status !== b.status) return a.status === "active" ? -1 : 1;
      const aTime = a.updatedAt?.seconds || 0;
      const bTime = b.updatedAt?.seconds || 0;
      return bTime - aTime;
    });
  }, [seriesList]);

  return {
    seriesList: sortedSeries,
    loading,
    createSeries,
    updateSeries,
    deleteSeries,
    closeSeries,
  };
}
